import { useState } from 'react';
import FileUpload from 'components/FileUpload';
import DownloadBox from 'components/DownloadBox';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';
import { toolAPI, mkUrl } from 'utils/api';

const PRESETS = [
  { label: 'HD (1280×720)', w: 1280, h: 720 },
  { label: 'Full HD (1920×1080)', w: 1920, h: 1080 },
  { label: 'Square 800px', w: 800, h: 800 },
  { label: 'Thumbnail (300×300)', w: 300, h: 300 },
  { label: 'Instagram Post (1080×1080)', w: 1080, h: 1080 },
  { label: 'Twitter Banner (1500×500)', w: 1500, h: 500 },
];

export default function ImageResize() {
  const [file, setFile]   = useState(null);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [fit, setFit]     = useState('contain');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dlUrl, setDlUrl] = useState('');

  const reset = () => { setFile(null); setWidth(''); setHeight(''); setError(''); setDlUrl(''); };

  const applyPreset = (p) => { setWidth(String(p.w)); setHeight(String(p.h)); };

  const handle = async () => {
    if (!file) { setError('Please select an image.'); return; }
    if (!width && !height) { setError('Enter at least a width or height.'); return; }
    setLoading(true); setError(''); setDlUrl('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (width)  fd.append('width', width);
      if (height) fd.append('height', height);
      fd.append('fit', fit);
      const res = await toolAPI.resizeImage(fd);
      setDlUrl(mkUrl(res.data));
    } catch (err) { setError(err.response?.data?.error || 'Resize failed.'); }
    finally { setLoading(false); }
  };

  return (
    <>
    <Seo title="Resize Image Online Free" description="Resize images to exact width and height. Free image resizer, no signup." path="/tools/image/resize" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name="resize" size={24} /></div>
            <div><h1>Image Resize</h1><p>Resize your image to exact dimensions or use a preset size.</p></div>
          </div>
        </div>

        {!dlUrl ? (
          <div className="tool-box">
            <FileUpload accept="image/*" onFileChange={setFile} hint="Supported: JPG, PNG, WEBP · Max 20MB" />

            {/* Presets */}
            <div style={{ marginTop: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--slate-700)', marginBottom: 10 }}>Quick Presets</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {PRESETS.map(p => (
                  <button key={p.label} className="btn btn-secondary btn-sm" onClick={() => applyPreset(p)} type="button">
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom size */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginTop: 20 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Width (px)</label>
                <input className="form-control" type="number" placeholder="e.g. 1280" value={width} onChange={e => setWidth(e.target.value)} min="1" max="8000" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Height (px)</label>
                <input className="form-control" type="number" placeholder="e.g. 720" value={height} onChange={e => setHeight(e.target.value)} min="1" max="8000" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Fit Mode</label>
                <select className="form-control" value={fit} onChange={e => setFit(e.target.value)}>
                  <option value="contain">Contain (no crop)</option>
                  <option value="cover">Cover (crop to fill)</option>
                  <option value="fill">Stretch to fit</option>
                  <option value="inside">Fit inside</option>
                </select>
              </div>
            </div>

            {error && <div className="alert alert-error" style={{ marginTop: 14 }}><Icon name="alertCircle" size={16} />{error}</div>}

            <button className="btn btn-primary btn-lg btn-full" style={{ marginTop: 20 }} onClick={handle} disabled={loading || !file}>
              {loading ? <><span className="spinner sp-sm" />Resizing...</> : <><Icon name="resize" size={18} />Resize Image</>}
            </button>
          </div>
        ) : (
          <DownloadBox url={dlUrl} filename="resized.jpg" onReset={reset} />
        )}

        <div className="alert alert-info" style={{ marginTop: 14 }}>
          <Icon name="info" size={16} />
          <span>You can enter just width OR just height — the other dimension will scale proportionally in Contain mode.</span>
        </div>
        <ToolSeoContent tool="image-resize" />
      </div>
    </div>
  </>
  );
}
